import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserActions } from "shared/hooks/user.actions";
import {
  TextField,
  Button,
  FormGroup,
  FormControl,
  Paper,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  LinearProgress,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  SvgIcon,
  Tooltip
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  CheckCircle,
  RadioButtonUnchecked,
  Error as ErrorIcon,
  Close,
  GitHub
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { styled } from "@mui/material/styles";
import { getOAuthUrl, OAUTH_PROVIDERS } from "../config/oauth";
import { axiosService } from "shared/helpers/axios";


const PASSWORD_LENGTH = 8;

// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(4),
  width: 380,
  textAlign: "center",
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(10px)",
  borderRadius: "16px",
  boxShadow: "0 8px 32px rgba(118, 75, 162, 0.15)",
  transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 12px 48px rgba(118, 75, 162, 0.25)"
  }
}));


const StyledTextField = styled(TextField)(({ theme }) => ({
    "& .MuiOutlinedInput-root": {
      transition: "all 0.2s ease-in-out",
      borderRadius: "12px",
      "&:hover": {
        boxShadow: "0 4px 12px rgba(118, 75, 162, 0.1)",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(118, 75, 162, 0.5)"
        }
      },
      "&.Mui-focused": {
        transform: "translateY(-1px)",
        boxShadow: "0 6px 16px rgba(118, 75, 162, 0.15)",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(118, 75, 162, 0.55)",
          borderWidth: "1px"
        }
      },
      // Remove yellow background on autofill
      "& input:-webkit-autofill": {
        WebkitBoxShadow: "0 0 0 100px white inset",
        WebkitTextFillColor: "inherit",
        backgroundColor: "transparent !important",
        transition: "background-color 5000s ease-in-out 0s"
      },
      "& input:-webkit-autofill:hover": {
        WebkitBoxShadow: "0 0 0 100px white inset",
        WebkitTextFillColor: "inherit",
        backgroundColor: "transparent !important"
      },
      "& input:-webkit-autofill:focus": {
        WebkitBoxShadow: "0 0 0 100px white inset",
        WebkitTextFillColor: "inherit",
        backgroundColor: "transparent !important"
      },
      "& input:-webkit-autofill:active": {
        WebkitBoxShadow: "0 0 0 100px white inset",
        WebkitTextFillColor: "inherit",
        backgroundColor: "transparent !important"
      }
    },
    "& .MuiInputLabel-root": {
      color: "rgba(0, 0, 0, 0.6)",
      "&.Mui-focused": {
        color: "rgba(118, 75, 162, 0.95)"
      }
    },
    "& .MuiFormHelperText-root": {
      margin: "4px 0 0 0",
      fontSize: "0.75rem",
      animation: "fadeIn 0.2s ease-out"
    }
  }));




const StyledButton = styled(Button)(({ theme }) => ({
  color: "white",
  backgroundColor: "rgba(118, 75, 162, 0.85)",
  padding: "12px 24px",
  borderRadius: "12px",
  textTransform: "none",
  fontSize: "1rem",
  fontWeight: 600,
  boxShadow: "0 4px 12px rgba(118, 75, 162, 0.25)",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "rgba(91, 54, 128, 0.95)",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 16px rgba(118, 75, 162, 0.35)"
  },
  "&:active": {
    transform: "translateY(0)",
    boxShadow: "0 2px 8px rgba(118, 75, 162, 0.25)"
  },
  "&:disabled": {
    backgroundColor: "rgba(118, 75, 162, 0.4)",
    color: "rgba(255, 255, 255, 0.7)"
  }
}));


// ---- OAuth provider icons ---- //

const GoogleIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </SvgIcon>
);

const OrcidIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0z" fill="#A6CE39"/>
    <path d="M8.5 7.2c0 .6-.5 1.1-1.1 1.1S6.3 7.8 6.3 7.2 6.8 6.1 7.4 6.1s1.1.5 1.1 1.1zM8.1 9.4H6.7v8.2h1.4V9.4z" fill="#fff"/>
    <path d="M11.3 9.4h-1.4v8.2h1.4v-4.8c0-1.2.3-1.8.6-2.1.3-.3.8-.5 1.4-.5 1.2 0 1.7.8 1.7 2.3v5.1H16.4v-5.4c0-2.1-1-3.2-2.8-3.2-1 0-1.7.4-2.2 1h-.1V9.4z" fill="#fff"/>
  </SvgIcon>
);

const OAuthButton = styled(Button)(({ theme }) => ({
  borderRadius: "12px",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "0.825rem",
  padding: 0,
  minWidth: "44px",
  width: "44px",
  height: "44px",
  border: "1px solid rgba(0, 0, 0, 0.12)",
  color: "rgba(0, 0, 0, 0.72)",
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  transition: "all 0.2s ease-in-out",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    borderColor: "rgba(118, 75, 162, 0.4)",
    transform: "translateY(-1px)",
    boxShadow: "0 3px 8px rgba(0, 0, 0, 0.08)",
  },
  "&:disabled": {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  "& .MuiButton-startIcon": {
    margin: 0,
  },
}));

// Password Strength Indicator Component
const PasswordStrengthIndicator = ({ password }) => {
  const getStrength = (pwd) => {
    if (!pwd) return 0;
    let score = 0;
    if (pwd.length >= 8) score += 25;
    if (/[A-Z]/.test(pwd)) score += 25;
    if (/[a-z]/.test(pwd)) score += 25;
    if (/[0-9]/.test(pwd)) score += 25;
    return score;
  };

  const strength = getStrength(password);
  const strengthText = 
    strength === 0 ? "Very Weak" :
    strength <= 25 ? "Weak" :
    strength <= 50 ? "Fair" :
    strength <= 75 ? "Good" : "Strong";
  
  const strengthColor = 
    strength === 0 ? "error" :
    strength <= 25 ? "error" :
    strength <= 50 ? "warning" :
    strength <= 75 ? "info" : "success";

  if (!password) return null;

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box sx={{ mt: 0.5, mb:0.5, px:1,  width: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
          <Typography variant="caption" color="textSecondary">
            Password Strength
          </Typography>
          <Typography 
            variant="caption" 
            fontWeight="bold" 
            color={`${strengthColor}.main`}
          >
            {strengthText}
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={strength} 
          color={strengthColor}
          sx={{ 
            height: 8, 
            borderRadius: 4,
            backgroundColor: "rgba(0,0,0,0.08)",
            "& .MuiLinearProgress-bar": {
              transition: "transform 0.3s ease"
            }
          }}
        />
      </Box>
    </motion.div>
  );
};

// Password Requirements Component
const PasswordRequirements = ({ password }) => {
  const requirements = [
    { text: "At least 8 characters", test: (pwd) => pwd.length >= 8 },
    { text: "One uppercase letter", test: (pwd) => /[A-Z]/.test(pwd) },
    { text: "One lowercase letter", test: (pwd) => /[a-z]/.test(pwd) },
    { text: "One number", test: (pwd) => /[0-9]/.test(pwd) }
  ];

  if (!password) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
    >
      <Box sx={{ mt: 2, p: 2, bgcolor: "rgba(118, 75, 162, 0.04)", borderRadius: 2 }}>
        <Typography variant="caption" color="textSecondary" gutterBottom display="block">
          Password Requirements:
        </Typography>
        <List dense disablePadding>
          {requirements.map((req, index) => (
            <ListItem key={index} disableGutters sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {req.test(password) ? (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 500, delay: index * 0.05 }}
                  >
                    <CheckCircle color="success" sx={{ fontSize: 18 }} />
                  </motion.div>
                ) : (
                  <RadioButtonUnchecked color="disabled" sx={{ fontSize: 18 }} />
                )}
              </ListItemIcon>
              <ListItemText 
                primary={req.text}
                primaryTypographyProps={{
                  variant: "body2",
                  color: req.test(password) ? "text.primary" : "text.secondary",
                  sx: { 
                    transition: "color 0.2s ease"
                  }
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    </motion.div>
  );
};

// Main Component
function LoginForm({ onSwitchToRegister }) {
  const navigate = useNavigate();
  const [validated, setValidated] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({ email: false, password: false });
  const [isLoading, setIsLoading] = useState(false);

  const [forgotMode, setForgotMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);

  const userActions = useUserActions();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setValidated(true);
    setTouched({ email: true, password: true });
    
    // Validate fields
    if (!form.email || !form.password) {
      setError("Please fill in all required fields");
      return;
    }

    setIsLoading(true);
    setError(null);

    const data = {
      email: form.email,
      password: form.password,
    };

    try {
      await userActions.login(data);
      navigate("/");
    } catch (err) {
      let errorMessage = "An unexpected error occurred";
      if (err.message) {
        try {
          const response = JSON.parse(err.request.response);
          errorMessage = response.message || response.error || err.request.response;
        } catch {
          errorMessage = err.request.response || err.message;
        }
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismissError = () => {
    setError(null);
  };

  const handleFieldChange = (field, value) => {
    setForm({ ...form, [field]: value });
    if (error) setError(null);
  };

  const isFormValid = form.email && form.password && form.password.length >= PASSWORD_LENGTH;

  const handleForgotSubmit = async () => {
    const email = resetEmail.trim();
    if (!email) { setResetError("Please enter your email address."); return; }
    setResetLoading(true);
    setResetError(null);
    try {
      await axiosService.post("/auth/password-reset/", { email });
      setResetSent(true);
    } catch (err) {
      const d = err.response?.data;
      setResetError(d?.error || d?.detail || "Something went wrong. Please try again.");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate data-testid="login-form">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, type: "spring", stiffness: 100 }}
      >
        <StyledPaper elevation={6}>
          {!forgotMode ? (
            <>
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <Typography
                  variant="h4"
                  align="center"
                  sx={{ color: "rgba(55, 44, 66, 0.85)", fontWeight: 500, mb: 3, letterSpacing: "-0.5px" }}
                >
                  Welcome Back
                </Typography>
              </motion.div>

              {/* Email Field */}
              <FormGroup>
                <FormControl error={validated && !form.email} fullWidth>
                  <StyledTextField
                    label="Email Address"
                    value={form.email}
                    onChange={(e) => handleFieldChange("email", e.target.value)}
                    onBlur={() => setTouched({ ...touched, email: true })}
                    required
                    type="email"
                    variant="outlined"
                    fullWidth
                    placeholder="name@example.com"
                    disabled={isLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color={validated && !form.email ? "error" : "action"} />
                        </InputAdornment>
                      ),
                      style: { fontSize: "1.2rem" },
                    }}
                    helperText={validated && !form.email ? "Email is required" : " "}
                    InputLabelProps={{ sx: { fontSize: "1.2rem", fontWeight: "500" } }}
                    sx={{ "& input::placeholder": { fontSize: "1.2rem" } }}
                  />
                </FormControl>
              </FormGroup>

              {/* Password Field */}
              <FormGroup>
                <FormControl error={validated && !form.password} fullWidth>
                  <StyledTextField
                    label="Password"
                    value={form.password}
                    onChange={(e) => handleFieldChange("password", e.target.value)}
                    onBlur={() => setTouched({ ...touched, password: true })}
                    required
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    fullWidth
                    placeholder="Enter your password"
                    disabled={isLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color={validated && !form.password ? "error" : "action"} />
                        </InputAdornment>
                      ),
                      style: { fontSize: "1.2rem" },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                            disabled={isLoading}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    helperText={validated && !form.password ? "Password is required" : " "}
                    InputLabelProps={{ sx: { fontSize: "1.2rem", fontWeight: "500" } }}
                  />
                </FormControl>
              </FormGroup>

              {/* Forgot password link */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -1.5, mb: 0.5 }}>
                <Button
                  size="small"
                  onClick={() => { setForgotMode(true); setResetEmail(form.email); setResetError(null); setResetSent(false); }}
                  sx={{
                    textTransform: "none",
                    color: "rgba(118, 75, 162, 0.95)",
                    fontSize: "0.9rem",
                    fontWeight: 500,
                    p: "2px 4px",
                    minWidth: "auto",
                    "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
                  }}
                >
                  Forgot password?
                </Button>
              </Box>

              {/* Error Alert */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Alert
                      severity="error"
                      action={
                        <IconButton aria-label="close" color="inherit" size="small" onClick={handleDismissError}>
                          <Close fontSize="inherit" />
                        </IconButton>
                      }
                      icon={<ErrorIcon />}
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        boxShadow: "0 4px 12px rgba(211, 47, 47, 0.15)",
                        textAlign: "left",
                        "& .MuiAlert-message": { fontSize: "0.875rem", color: "rgba(211, 47, 47, 0.95)" },
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Login Failed</strong>
                        <br />
                        {error}
                      </Typography>
                    </Alert>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Submit Button */}
              <StyledButton type="submit" variant="contained" fullWidth disabled={isLoading || (validated && !isFormValid)}>
                {isLoading ? (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    Signing in...
                  </motion.div>
                ) : (
                  "Sign In"
                )}
              </StyledButton>

              {/* OAuth section */}
              <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center", mt: 1 }}>
                <Typography variant="body1" align="center" sx={{ mt: 2, color: "text.secondary" }}>
                  or continue with
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: "center", alignItems: "center", mt: 2 }}>
                <Divider sx={{ flex: 1, borderColor: "text.secondary" }} />
                {OAUTH_PROVIDERS.google.clientId && (
                  <Tooltip title="Sign in with Google" arrow>
                    <OAuthButton variant="outlined" startIcon={<GoogleIcon />} onClick={() => { window.location.href = getOAuthUrl("google"); }} disabled={isLoading} />
                  </Tooltip>
                )}
                {/* {OAUTH_PROVIDERS.github.clientId && (
                  <Tooltip title="Sign in with GitHub" arrow>
                    <OAuthButton variant="outlined" startIcon={<GitHub sx={{ color: "#333" }} />} onClick={() => { window.location.href = getOAuthUrl("github"); }} disabled={isLoading}>
                      GitHub
                    </OAuthButton>
                  </Tooltip>
                )}
                {OAUTH_PROVIDERS.orcid.clientId && (
                  <Tooltip title="Sign in with ORCID" arrow>
                    <OAuthButton variant="outlined" startIcon={<OrcidIcon />} onClick={() => { window.location.href = getOAuthUrl("orcid"); }} disabled={isLoading}>
                      ORCID
                    </OAuthButton>
                  </Tooltip>
                )} */}
                <Divider sx={{ flex: 1, borderColor: "text.secondary" }} />
              </Box>

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                <Typography variant="body1" align="center" sx={{ mt: 2, color: "text.secondary" }}>
                  Don't have an account?{" "}
                  <Button
                    variant="filled"
                    sx={{
                      color: "rgba(118, 75, 162, 0.85)",
                      textTransform: "none",
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      p: 0,
                      minWidth: "auto",
                      "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
                    }}
                    onClick={() => onSwitchToRegister ? onSwitchToRegister() : navigate("/register/")}
                  >
                    Sign up
                  </Button>
                </Typography>
              </motion.div>
            </>
          ) : (
            <>
              <Typography
                variant="h4"
                align="center"
                sx={{ color: "rgba(55, 44, 66, 0.85)", fontWeight: 500, mb: 1.5, letterSpacing: "-0.5px" }}
              >
                Reset Password
              </Typography>

              {!resetSent ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                    Enter your email and we'll send you a link to reset your password.
                  </Typography>

                  {resetError && (
                    <Alert severity="error" onClose={() => setResetError(null)} sx={{ mb: 2, borderRadius: 2, textAlign: "left" }}>
                      {resetError}
                    </Alert>
                  )}

                  <StyledTextField
                    label="Email Address"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    fullWidth
                    disabled={resetLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color="action" />
                        </InputAdornment>
                      ),
                      style: { fontSize: "1.2rem" },
                    }}
                    InputLabelProps={{ sx: { fontSize: "1.2rem", fontWeight: "500" } }}
                  />

                  <StyledButton
                    fullWidth
                    onClick={handleForgotSubmit}
                    disabled={resetLoading || !resetEmail.trim()}
                    sx={{ mt: 2.5 }}
                  >
                    {resetLoading ? "Sending…" : "Send Reset Link"}
                  </StyledButton>
                </>
              ) : (
                <Alert severity="success" sx={{ mb: 2, borderRadius: 2, textAlign: "left" }}>
                  Check your inbox — a reset link has been sent to <strong>{resetEmail}</strong>.
                  If you don't see it, check your spam folder.
                </Alert>
              )}

              <Button
                fullWidth
                onClick={() => { setForgotMode(false); setResetSent(false); setResetError(null); }}
                sx={{
                  mt: 2,
                  textTransform: "none",
                  color: "rgba(118, 75, 162, 0.85)",
                  fontWeight: 500,
                  "&:hover": { backgroundColor: "rgba(118, 75, 162, 0.06)" },
                }}
              >
                ← Back to Sign In
              </Button>
            </>
          )}
        </StyledPaper>
      </motion.div>
    </form>
  );
}

export default LoginForm;